import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-key-model-explainer.css?inline';

/** One custody model in plain words. */
export interface PkCustodyModel {
    custody: 'customer' | 'escrowed' | 'provider' | 'byok';
    title: string;
    summary: string;
    holds: string;
    decrypts: string;
    recovery: string;
}

/** The four custody models of the PortalKit keys spec (§1), in plain words. */
export const PK_CUSTODY_MODELS: readonly PkCustodyModel[] = [
    {
        custody: 'customer',
        title: 'You hold the key',
        summary: 'Your private key stays with you — on your server, in your plugin. We only keep its public certificate.',
        holds: 'You, on your own systems.',
        decrypts: 'Only you. We cannot read your data.',
        recovery: 'None. If you lose the private key, what it protects cannot be recovered — by you or by us.',
    },
    {
        custody: 'escrowed',
        title: 'You hold the key, with a locked spare',
        summary: 'Your private key stays with you, and a copy locked with a recovery password you chose is stored with us. Your browser locks and unlocks it; we never see the password or the key.',
        holds: 'You, plus a password-locked copy in our store.',
        decrypts: 'Only you. The copy is unlocked in your browser, never on our servers.',
        recovery: 'Your recovery password. We cannot reset it or unlock the copy for you.',
    },
    {
        custody: 'provider',
        title: 'We hold the key for you',
        summary: 'A key in our hardware-backed key store protects your data. Our service uses it on your behalf; your Owners control it — rotate, disable, or destroy it.',
        holds: 'Our key store (HSM-backed).',
        decrypts: 'Our service, on your behalf, while the key is enabled.',
        recovery: 'A disabled key can be re-enabled. A destroyed key cannot: destroying it is a permanent, deliberate shred.',
    },
    {
        custody: 'byok',
        title: 'Your key locks ours',
        summary: 'Bring your own key: a key in your own key vault wraps the key we use for your data. Turn yours off and we can no longer read your data.',
        holds: 'Your key vault holds the outer key; ours holds the inner one.',
        decrypts: 'Our service, only while your key is enabled.',
        recovery: 'You control the switch: re-enable your key to restore access.',
    },
];

/**
 * `<pk-key-model-explainer>` — a plain-words card per custody model: who holds the key, who can decrypt, what
 * recovery means. With `custody` set it explains the tenant's own model; without it, all four side by side.
 *
 * Attributes:
 *   - `custody`   — `customer` | `escrowed` | `provider` | `byok`; absent or unknown shows all four.
 *   - `heading`   — optional heading above the card(s).
 */
export class PkKeyModelExplainer extends HTMLElement {
    static readonly tagName = 'pk-key-model-explainer';

    static get observedAttributes(): string[] {
        return ['custody', 'heading'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void { this._render(); }
    attributeChangedCallback(): void { if (this.isConnected) this._render(); }

    private template(): TemplateResult {
        const custody = (this.getAttribute('custody') ?? '').trim().toLowerCase();
        const one = PK_CUSTODY_MODELS.find((m) => m.custody === custody);
        const models = one ? [one] : PK_CUSTODY_MODELS;
        const heading = this.getAttribute('heading') ?? '';
        return html`
            <style>${styles}</style>
            ${heading ? html`<h3 class="heading">${heading}</h3>` : ''}
            <div class="models ${one ? 'single' : 'all'}">
                ${models.map((m) => html`
                    <section class="model" data-custody=${m.custody}>
                        <p class="eyebrow">${m.custody}</p>
                        <h4 class="title">${m.title}</h4>
                        <p class="summary">${m.summary}</p>
                        <dl>
                            <dt>Who holds it</dt><dd class="holds">${m.holds}</dd>
                            <dt>Who can decrypt</dt><dd class="decrypts">${m.decrypts}</dd>
                            <dt>Recovery</dt><dd class="recovery">${m.recovery}</dd>
                        </dl>
                    </section>
                `)}
            </div>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkKeyModelExplainer.tagName)) {
    customElements.define(PkKeyModelExplainer.tagName, PkKeyModelExplainer);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-key-model-explainer': PkKeyModelExplainer;
    }
}
